// Keep PDFs, GIF/SVG animations, small files and unsupported browsers unchanged.
async function hasEmbeddedHdrOrTrailer(file) {
  if (!file || file.type !== "image/jpeg" || typeof file.arrayBuffer !== "function") return false;
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const end = bytes.length;
    if (
      end >= 4 &&
      bytes[end - 4] === 0x53 &&
      bytes[end - 3] === 0x45 &&
      bytes[end - 2] === 0x46 &&
      bytes[end - 1] === 0x54
    )
      return true;
    const limit = Math.min(end, 131072);
    let head = "";
    for (let i = 0; i < limit; i += 4096) {
      head += String.fromCharCode.apply(null, bytes.subarray(i, Math.min(limit, i + 4096)));
    }
    return (
      head.includes("hdr-gain-map") ||
      head.includes("urn:iso:std:iso:ts:21496:-1") ||
      head.includes("MPF\0")
    );
  } catch {
    return false;
  }
}

export async function optimizeImageFormData(data) {
  if (
    typeof window === "undefined" ||
    typeof FormData === "undefined" ||
    !(data instanceof FormData)
  )
    return data;
  if (
    typeof data.get !== "function" ||
    typeof data.getAll !== "function" ||
    typeof data.entries !== "function" ||
    typeof Image === "undefined" ||
    typeof URL === "undefined" ||
    typeof URL.createObjectURL !== "function" ||
    typeof URL.revokeObjectURL !== "function"
  )
    return data;
  const file = data.get("file");
  if (
    !file ||
    !["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
    data.getAll("file").length !== 1
  )
    return data;
  const stripMetadata = file.size <= 256 * 1024 ? await hasEmbeddedHdrOrTrailer(file) : false;
  if (file.size <= 256 * 1024 && !stripMetadata) return data;
  let objectUrl;
  try {
    objectUrl = URL.createObjectURL(file);
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = reject;
      element.src = objectUrl;
    });
    if (
      !image.naturalWidth ||
      !image.naturalHeight ||
      image.naturalWidth * image.naturalHeight > 32000000
    )
      return data;
    const scale = Math.min(
      1,
      1920 / Math.max(image.naturalWidth, image.naturalHeight)
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext("2d");
    if (!context || typeof canvas.toBlob !== "function") return data;
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const compressed = await new Promise((resolve) =>
      canvas.toBlob(resolve, file.type, 0.84)
    );
    if (!compressed || compressed.type !== file.type || !compressed.size)
      return data;
    const mustStrip =
      stripMetadata ||
      (compressed.size >= file.size && (await hasEmbeddedHdrOrTrailer(file)));
    if (compressed.size >= file.size && !mustStrip) return data;
    const optimized = new FormData();
    for (const [name, value] of data.entries()) {
      if (name === "file")
        optimized.append(name, compressed, file.name || "image");
      else if (value instanceof Blob)
        optimized.append(name, value, value.name || "file");
      else optimized.append(name, value);
    }
    return optimized;
  } catch {
    return data;
  } finally {
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  }
}
